let env;
let cloudFrontURL;
let hostUrl;
let redirect_uri;
let logout_uri;
let apiPort;
let environment;
let toyotaConnectedServicesUrl;
let buckets;
let cookieDomain;
let auth;

// IDENTITY PROVIDER — each env signs in with exactly one provider, described by
// its `auth` block. Build it with forgeRockAuth or entraAuth; everything
// provider-specific lives inside that block, and services/auth/providers turns
// it into behavior. appsettings.test.js checks every env's block.
//
// Moving an env to Entra also needs, outside this file: the index.html CSP
// (login.microsoftonline.com in connect-src and frame-src) and the app
// registration's redirect (/auth/callback) and post-logout (/logout/callback)
// URIs for the env's host.

const FORGEROCK_REALM_PATH = "/openam/oauth2/realms/root/realms/dealerdaily";
const FORGEROCK_SERVICE = "engage";

// ForgeRock signs in through the Engage /login page (loginPageUrl stands in for
// the authorize endpoint), which runs AM's authentication journey itself.
//   amOrigin         — the AM host, e.g. https://ep.fram.idm.toyota.com
//   idleTimeoutMs    — renewal is declined once the user has been idle this
//                      long (see shouldRenewInBackground). The real bound on an
//                      idle session is this plus up to one token lifetime.
//   resetPasswordUrl — the forgotten-password page (on the FRIG host)
//   federation       — distributor/corporate IdP entry points on /login
const forgeRockAuth = ({
  amOrigin,
  loginPageUrl,
  idleTimeoutMs,
  resetPasswordUrl,
  federation,
  framEndpoint = `${amOrigin}/openam`,
}) => ({
  provider: "forgerock",
  forgerock: {
    wellKnownUrl: `${amOrigin}${FORGEROCK_REALM_PATH}/.well-known/openid-configuration`,
    authorizeUrl: `${amOrigin}${FORGEROCK_REALM_PATH}/authorize`,
    // The browser and the app's WebView are separate AM clients.
    clientIds: { browser: "Engage", appWebView: "EngageMobile" },
    // The realm registers only [address, phone, openid, profile, email]; any
    // other scope comes back as error=invalid_scope and loops the callback.
    scope: "openid profile",
    loginPageUrl,
    idleTimeoutMs,
    framEndpoint,
    journeyPath: `/json/realms/root/realms/dealerdaily/authenticate?service=${FORGEROCK_SERVICE}&authIndexType=service&authIndexValue=${FORGEROCK_SERVICE}`,
    webauthnRpName: "ForgeRock",
    resetPasswordUrl,
    federation,
  },
});

// Entra signs in on Microsoft's hosted pages; the Engage /login page is not
// used at all.
//   allowedDomainHints — the domain_hint values this env forwards to Microsoft
//                        so distributor users skip home-realm discovery (see
//                        providers/entra/domainHint). Prod differs from QA.
const entraAuth = ({ tenantId, clientId, allowedDomainHints = [] }) => ({
  provider: "entra",
  entra: {
    tenantId,
    clientId,
    wellKnownUrl: `https://login.microsoftonline.com/${tenantId}/v2.0/.well-known/openid-configuration`,
    // offline_access yields a refresh token, which signinSilent prefers over
    // the hidden iframe that browser storage partitioning breaks.
    scope: "openid profile offline_access",
    // The token the Engage API authorizes on. The ID token, because the app
    // registration exposes no API scope: its access token is for Microsoft
    // Graph and the API can't validate it. Exposing a scope (and requesting
    // it) would make "access_token" the right value here.
    apiBearer: "id_token",
    allowedDomainHints,
  },
});

const prodFederation = {
  tmnaUrl:
    "https://login.microsoftonline.com/8c642d1d-d709-47b0-ab10-080af10798fb/saml2",
  tmnaSamlToken:
    "jZFNa8JAEIb%2fSti72VU3NC5JIH6BYKFo7aG3IRkxsB%2fpzkZqf31XxVOheB3mnZnnmYLA6F7VQzjZHX4NSCH5NtpSyQZvlQPqSFkwSCo0al%2b%2fbtUkFcpggBYCsGSzLNk6l8s8z%2bt6OpdyMZ7nUk5fslqu8slYLGcrlnygp87ZksVwzBANuLEUwIZYEhM5ErORkO8iU2KmxlkqM%2fF57XsDou6MJTuCJmRJTYQ%2bxEkLZ2kw6Pfoz12Dh922ZKcQelKcY58ePZi0a00a3MUFSBtnVDyKux4jDH%2bk%2bZWj1h0QbxE0%2bhY6feHUs7sEdbPzv4reu%2bAap1mydr7Bm8nHvVVxQ%2fXPKIUHGqueAIGfwSO0cXlb8PuSquB%2fn1n9Ag%3d%3d",
  setUrl:
    "https://setfederationgateway.jmfamily.com/Saml20/IdpInitiated?Realm=https%3A%2F%2Fep.fram.idm.toyota.com%2Fset",
  gstUrl: "https://idp.gstoyota.com/tmna-forgerock",
};

const qaFederation = {
  tmnaUrl:
    "https://login.microsoftonline.com/8c642d1d-d709-47b0-ab10-080af10798fb/saml2",
  tmnaSamlToken:
    "jZFNa8JAEIb%2fSti72Ri3uF2SQPwCwULR2kNvQzJiYD%2fizkZqf31XxVOheB1m3pnnmYLA6F7VQzjaLZ4GpJB8G22pZIO3ygF1pCwYJBUatavfNipPM2UwQAsBWLJelGwlxUJKWdeTmRDz8UwKMZm%2b1GIp83G2eF2y5BM9dc6WLA7HGaIB15YC2BBLWS5GmRxlk488V5lUIjaNp1%2fXvncg6s5YsgNoQpbUROhDTJo7S4NBv0N%2f7hrcbzclO4bQk%2bIc%2b%2fTgwaQnSLvWpMFdXIC0cUbFu7jrMfLwRwC%2fotS6A%2bItgkbfQqcvnHp296Bugv630XsXXOM0S1bON3iT%2bTi5Km60%2fhmr8KBj1XMs8DN4PEHB7zuqgv99Z%2fUL&",
  setUrl:
    "https://setfederationgateway.uat.jmfamily.com/Saml20/IdpInitiated?Realm=https%3A%2F%2Fep.fram.qa.idm.toyota.com%2Fset_stage",
  gstUrl: "https://idp.dev.gstoyota.com/tmna-forgerock",
};

// The Entra POC tenant and app registration that dev and local share.
const entraPoc = {
  tenantId: "417ca393-15d2-4e55-beec-87affb35daa4",
  clientId: "690524e4-5be3-4422-84e9-10b07ee42c65",
  allowedDomainHints: ["gstdealerqa.com", "setdealerdailystage.com"],
};

const hostname = globalThis.location?.hostname;

// The following block of consts were made due to Sonarqube quiality gate issues.
// I advise not deleting them unless Sonarqube policies are changed or else
// it could lead to build failures.
const nonprodTcuvUrl =
  "https://tcuvstaging.hugheshunter.com/?option=saml_user_login&idp=Tess";
const nonprodTriviaUrl = "https://staging.engagetrivia.com";
const nonprodToyotaConnectedUrl =
  "https://oneportaltoyotadealer.stg.telematicsct.com/us";
const engageDevUrl = "https://dev.nonprod.engage.toyota.com";
const puertoRico = "Puerto Rico";
const regionalManager = "Regional Manager";
const dealerManager = "Dealer Manager";
const salesAssociate = "Sales Associate";

switch (hostname) {
  case "engage.toyota.com":
    env = "prod";
    break;
  case "staging.engage.toyota.com":
    env = "staging";
    break;
  case "test.nonprod.engage.toyota.com":
    env = "test";
    break;
  case "dev.nonprod.engage.toyota.com":
    env = "dev";
    break;
  default:
    env = "local";
    break;
}

switch (env) {
  case "prod":
    hostUrl = "https://engage.toyota.com";
    redirect_uri = "https://engage.toyota.com/auth/callback";
    auth = forgeRockAuth({
      amOrigin: "https://ep.fram.idm.toyota.com",
      loginPageUrl: "https://engage.toyota.com/login",
      idleTimeoutMs: 60 * 60 * 1000,
      resetPasswordUrl:
        "https://ep.frig.idm.toyota.com/PasswordReset#/PasswordReset",
      federation: prodFederation,
    });
    cookieDomain = ".toyota.com";
    logout_uri = "https://engage.toyota.com/logout/callback";
    apiPort = "443";
    environment = "prod";
    cloudFrontURL = "https://engage.toyota.com";
    toyotaConnectedServicesUrl =
      "https://oneportaltoyotadealer.telematicsct.com/us";
    buckets = {
      urlFiles: "https://engage.toyota.com/",
    };
    break;
  case "staging":
    hostUrl = "https://staging.engage.toyota.com";
    redirect_uri = "https://staging.engage.toyota.com/auth/callback";
    auth = forgeRockAuth({
      amOrigin: "https://ep.fram.idm.toyota.com",
      loginPageUrl: "https://staging.engage.toyota.com/login",
      idleTimeoutMs: 60 * 60 * 1000,
      resetPasswordUrl:
        "https://ep.frig.idm.toyota.com/PasswordReset#/PasswordReset",
      federation: prodFederation,
    });
    cookieDomain = ".toyota.com";
    logout_uri = "https://staging.engage.toyota.com/logout/callback";
    apiPort = "443";
    environment = "staging";
    cloudFrontURL = "https://staging.engage.toyota.com";
    toyotaConnectedServicesUrl = nonprodToyotaConnectedUrl;
    buckets = {
      urlFiles: "https://staging.engage.toyota.com/",
    };
    break;
  case "test":
    hostUrl = "https://test.nonprod.engage.toyota.com";
    redirect_uri = "https://test.nonprod.engage.toyota.com/auth/callback";
    auth = forgeRockAuth({
      amOrigin: "https://ep.fram.qa.idm.toyota.com",
      loginPageUrl: "https://test.nonprod.engage.toyota.com/login",
      idleTimeoutMs: 8 * 60 * 1000,
      resetPasswordUrl:
        "https://ep.frig.qa.idm.toyota.com/PasswordReset#/PasswordReset",
      federation: qaFederation,
    });
    cookieDomain = ".toyota.com";
    logout_uri = "https://test.nonprod.engage.toyota.com/logout/callback";
    apiPort = "443";
    environment = "test";
    cloudFrontURL = "https://test.nonprod.engage.toyota.com";
    toyotaConnectedServicesUrl = nonprodToyotaConnectedUrl;
    buckets = {
      urlFiles: "https://test.nonprod.engage.toyota.com/",
    };
    break;
  case "dev":
    hostUrl = engageDevUrl;
    redirect_uri = "https://dev.nonprod.engage.toyota.com/auth/callback";
    auth = entraAuth(entraPoc);
    cookieDomain = ".toyota.com";
    logout_uri = "https://dev.nonprod.engage.toyota.com/logout/callback";
    apiPort = "443";
    environment = "dev";
    cloudFrontURL = engageDevUrl;
    toyotaConnectedServicesUrl = nonprodToyotaConnectedUrl;
    buckets = {
      urlFiles: "https://dev.nonprod.engage.toyota.com/",
    };
    break;
  case "local":
    hostUrl = "http://localhost";
    redirect_uri = "http://localhost:3000/auth/callback";
    auth = entraAuth(entraPoc);
    cookieDomain = "localhost";
    logout_uri = "http://localhost:3000/logout/callback";
    apiPort = "8080";
    environment = "dev";
    cloudFrontURL = engageDevUrl;
    toyotaConnectedServicesUrl = nonprodToyotaConnectedUrl;
    buckets = {
      urlFiles: "https://dev.nonprod.engage.toyota.com/",
    };
    break;
  default:
    throw new Error("Environment variable not set correctly");
}

export default {
  ContactEmail: "contact.us.engage@toyota.com",
  hostUrl: hostUrl,
  buckets: buckets,
  redirect_uri: redirect_uri,
  logout_uri: logout_uri,
  auth,
  apiPath: "/api",
  apiPort: apiPort,
  environment: environment,
  cloudFrontURL: cloudFrontURL,
  BasePath: "",
  cookieDomain: cookieDomain,
  AWSImgRepo: {
    amm_v2: `${cloudFrontURL}/static/images/amm_v2/`,
    announcements: `${cloudFrontURL}/static/images/announcements/`,
    notifications: `${cloudFrontURL}/static/images/notifications/`,
    articleImages: `${cloudFrontURL}/static/articles`,
    beyondZero: `${cloudFrontURL}/static/images/beyondZero/`,
    certification: `${cloudFrontURL}/static/images/certification/en/`,
    contact: `${cloudFrontURL}/static/images/contact/`,
    cloudFrontURL,
    edge: `${cloudFrontURL}/static/images/edge/`,
    nitroOverview: `${cloudFrontURL}/static/images/nitroOverview/`,
    engageXP: `${cloudFrontURL}/static/images/engageXP/`,
    ev: `${cloudFrontURL}/static/images/ev/`,
    glossary: `${cloudFrontURL}/static/images/glossary/`,
    homepage_v3: `${cloudFrontURL}/static/images/homepage_v3/`,
    jellybeans: `${cloudFrontURL}/static/images/jellybeans/`,
    dynamicImages: (page, content, version) =>
      `${cloudFrontURL}/static/content/${page}/${content}/${version}/`,
    resources: `${cloudFrontURL}/static/images/resources/`,
    smartPathV2: `${cloudFrontURL}/static/images/smartPathV2/en/`,
    static: `${cloudFrontURL}/static`,
    towingPayload: `${cloudFrontURL}/static/images/towing_payload_101/`,
    vehicle: `${cloudFrontURL}/static/images/vehicle/`,
    tournament: `${cloudFrontURL}/static/content/tournament/ev/1/`,
  },
  ScreenWidthBreakpoints: {
    small: 640,
    medium: 1024,
  },
  PaintHexCodes: {
    "040": "#FFFFFF",
    "058": "#efeeed",
    "062": "#f0eeeb",
    "068": "#f4f3f2",
    "070": "#F3F2F2",
    "082": "#f5f7ed",
    "084": "#f3f3f2",
    "089": "#e3e9e9",
    K1X: "#e0ddd4",
    202: "#000000",
    209: "#1d1e1e",
    218: "#00031E",
    219: "#1d1c1c",
    221: "#0d2c37",
    "41W": "#080808",
    D4S: "#191918",
    "61K": "#40403F",
    "42A": "#3a3a3a",
    "1D4": "#dcdce0",
    "1D6": "#c6c8cc",
    "1D7": "#d5d5d5",
    "1E0": "#3e3d44",
    "1E7": "#e5e5e9",
    "1F7": "#808080",
    "1F8": "#99a4aa",
    "1F9": "#3d424a",
    "1G2": "#767471",
    "1G3": "#494848",
    "1G6": "#a0a9a8",
    "1H1": "#63615e",
    "1H2": "#062438",
    "1H5": "#737B82",
    "1J6": "#d1d4d7",
    "1J9": "#828387",
    "1K0": "#a4a09f",
    A4D: "#cfcfcf",
    G1U: "#808080",
    "38P": "#7a797a",
    "3L5": "#d3202a",
    "3P0": "#b1160c",
    "3P2": "#420515",
    "3Q3": "#8d1a2c",
    "3Q7": "#5f0426",
    "3Q8": "#842020",
    "3R8": "#5a1414",
    "3R0": "#4c1b31",
    "3R3": "#a21e22",
    "3S7": "#f92d68",
    "3T0": "#470012",
    "3T3": "#740000",
    "3T7": "#940d0e",
    M7Y: "#b2040b",
    C7P: "#660000",
    "41G": "#9f1204",
    "45P": "#a8a8a9",
    "4Q2": "#b7aa86",
    "4R3": "#b29d76",
    "4R8": "#b9370a",
    "4S6": "#4f0b04",
    "4T3": "#776352",
    "4T6": "#d58035",
    "4T8": "#c3b79d",
    "4U2": "#7f7056",
    "4U5": "#382c22",
    "4U3": "#673f18",
    "4V6": "#af9a7c",
    "4V7": "#e86542",
    "4V8": "#605c55",
    "4W2": "#d15329",
    "4W4": "#4f3b35",
    "4X0": "#d63400",
    "4X1": "#beb8b8",
    "4X3": "#f87907",
    "4X4": "#412C1B",
    "4X7": "#372b23",
    H8R: "#992d0b",
    588: "#acc036",
    "5A3": "#f8a805",
    "5B2": "#C7B7A3",
    "4V0": "#77675c",
    NBB: "#c13d27",
    "6T3": "#122d2c",
    GT7: "#767c67",
    "6T8": "#224234",
    "6U0": "#adb59d",
    "6V2": "#384021",
    "6V4": "#2c2e15",
    "6V7": "#2a3a2a",
    "6W2": "#a9a100",
    "6W4": "#5C625C",
    "6W7": "#20371f",
    "6W9": "#2b7a78",
    774: "#5fa8aa",
    776: "#576c67",
    777: "#698580",
    779: "#266d6b",
    781: "#94adaf",
    785: "#013341",
    787: "#d0e0ea",
    788: "#0a5d6b",
    791: "#458a96",
    "8N0": "#7491af",
    "8P1": "#056fb7",
    "8P8": "#032945",
    "8R3": "#406080",
    "8R5": "#7ca1ac",
    "8S4": "#78898d",
    "8S6": "#1b1a49",
    "8S7": "#788cb2",
    "8T0": "#15499c",
    "8T1": "#3d5767",
    "8T5": "#273476",
    "8T7": "#0359a6",
    "8U6": "#2b5e7f",
    "8U8": "#a1b2b9",
    "8V1": "#2d3544",
    "8V5": "#42556d",
    "8V7": "#70a3bb",
    "8W1": "#8eaece",
    "8W2": "#2a5180",
    "8W6": "#102740",
    "8W7": "#111d69",
    "8X2": "#354c9c",
    K3X: "#2b2373",
    "42M": "#001644",
    "44J": "#043589",
    "8X7": "#0a5e96",
    "8Y1": "#05678b",
    E8H: "#03193c",
    "9AG": "#969fb9",
    "9AH": "#2d141c",
    "4U0": "#BCAA97",
    792: "#468CA3",
    PBA: "#43434B",
    "1K4": "#ADB4B8",
    "4X9": "#72605A",
    "8W9": "#26A0DE",
    "6X1": "#857A6A",
    "3U4": "#A41F1C",
    "3U5": "#E20500",
    DAR: "#0F7AFF",
    "8T6": "#19529b",
    // TODO: Replace if business wants/add 2 tone functionality
    "2PS": "#e3e9e9",
    "2PT": "#828387",
    "2QQ": "#0359a6",
    "2NG": "#354c9c",
    "2NE": "#740000",
    "2NJ": "#2b7a78",
    "2PN": "#740000",
    "2PP": "#354c9c",
    "2MR": "#d1d4d7",
    "2MS": "#1d1c1c",
    "2MT": "#1b1a49",
    "2NL": "#f0eeeb",
    "2NM": "#8d1a2c",
    "2NV": "#05678b",
    "2NA": "#F3F2F2",
    "2RV": "#26A0DE",
    "2NF": "#740000",
    "2NK": "#A4A09F",
    "47A": "#BBBEBD",
    "2QU": "#949B96",
    "2QV": "#26A0DE",
    "2QW": "#00031E",
    "2QJ": "#F3F2F2",
    "2QY": "#C6C8CC",
    "2QZ": "#494848",
    "2RA": "#303B76",
    "6X3": "#949B96",
    "1K3": "#636B79",
    "2SC": "#E20500",
    D01: "#EBEAE5",
    D07: "#1D306F",
    D06: "#DDB329",
    D04: "#161617",
    D05: "#B61E0D",
    D02: "#DCDCDE",
    D03: "#B0B7BD",
    D08: "#CCD0D3",
    P8Y: "#79787E",
    DAN: "#285A38",
    "2UD": "#857A6A",
    "2UF": "#808080",
    "2UG": "#A41F1C",
    "2UH": "#26A0DE",
    "2QX": "#494848",
    "2NH": "#354c9c",
    "2TA": "#1d1e1e",
    "2TC": "#992d0b",
    "2TB": "#E20500",
    "1K5": "#8898AC",
    "8X8": "#1E2548",
  },
  Regions: {
    10: "TMS",
    11: "Los Angeles",
    110: "Los Angeles Corporate",
    12: "San Francisco",
    120: "San Francisco Corporate",
    13: "Portland",
    130: "Portland Corporate",
    15: "Denver",
    150: "Denver Corporate",
    16: "New York",
    160: "New York Corporate",
    17: "Boston",
    170: "Boston Corporate",
    21: "Chicago",
    210: "Chicago Corporate",
    22: "Cincinnati",
    220: "Cincinnati Corporate",
    23: "Kansas City",
    230: "Kansas City Corporate",
    30: "Lexus HQ",
    300: "Lexus HQ Corporate",
    50: "Southeast",
    500: "Southeast Corporate",
    60: "Gulf States",
    600: "Gulf States Corporate",
    80: "CATD",
    800: "CATD Corporate",
    81: "21SNovemRegion",
    82: puertoRico,
    820: "Puerto Rico Corporate",
    83: "Mexico",
    830: "Mexico Corporate",
  },
  BATIncentivesRegions: {
    "Los Angeles": "socal",
    "Los Angeles Corporate": "socal",
    "San Francisco": "norcal",
    "San Francisco Corporate": "norcal",
    Portland: "pacificnorthwest",
    "Portland Corporate": "pacificnorthwest",
    Denver: "denver",
    "Denver Corporate": "denver",
    "New York": "greaterny",
    "New York Corporate": "greaterny",
    Boston: "newengland",
    "Boston Corporate": "newengland",
    Chicago: "midwest",
    "Chicago Corporate": "midwest",
    Cincinnati: "mi-oh-ky-tn",
    "Cincinnati Corporate": "mi-oh-ky-tn",
    "Kansas City": "central",
    "Kansas City Corporate": "central",
    Southeast: "southeasttoyota",
    "Southeast Corporate": "southeasttoyota",
    CATD: "centralatlantic",
    "CATD Corporate": "centralatlantic",
    "Gulf States": "gst",
    "Gulf States Corporate": "gst",
  },
  JobCode: {
    0: "Team Member",
    "086": "TMS",
    "020": regionalManager,
    "061": regionalManager,
    "071": regionalManager,
    "072": regionalManager,
    "073": regionalManager,
    "074": regionalManager,
    "075": regionalManager,
    "076": regionalManager,
    "078": regionalManager,
    "079": regionalManager,
    "093": regionalManager,
    "094": regionalManager,
    "096": regionalManager,
    1: regionalManager,
    103: regionalManager,
    104: regionalManager,
    106: regionalManager,
    107: regionalManager,
    120: regionalManager,
    121: regionalManager,
    122: regionalManager,
    123: regionalManager,
    124: regionalManager,
    125: regionalManager,
    126: regionalManager,
    127: regionalManager,
    128: regionalManager,
    129: regionalManager,
    142: regionalManager,
    143: regionalManager,
    "001": dealerManager,
    "002": dealerManager,
    "003": dealerManager,
    "004": dealerManager,
    "005": dealerManager,
    "006": dealerManager,
    "007": dealerManager,
    "008": dealerManager,
    "009": dealerManager,
    "010": dealerManager,
    "017": dealerManager,
    "018": dealerManager,
    "025": dealerManager,
    "029": dealerManager,
    "032": dealerManager,
    "034": dealerManager,
    "064": dealerManager,
    115: dealerManager,
    131: dealerManager,
    132: dealerManager,
    133: dealerManager,
    139: dealerManager,
    141: dealerManager,
    144: dealerManager,
    158: dealerManager,
    173: dealerManager,
    110: dealerManager,
    "000": salesAssociate,
    "011": salesAssociate,
    "012": salesAssociate,
    "013": salesAssociate,
    "014": salesAssociate,
    "015": salesAssociate,
    "016": salesAssociate,
    "021": salesAssociate,
    "022": salesAssociate,
    "023": salesAssociate,
    "024": salesAssociate,
    "026": salesAssociate,
    "027": salesAssociate,
    "028": salesAssociate,
    "030": salesAssociate,
    "031": salesAssociate,
    "033": salesAssociate,
    "036": salesAssociate,
    "038": salesAssociate,
    "039": salesAssociate,
    "040": salesAssociate,
    "041": salesAssociate,
    "042": salesAssociate,
    "043": salesAssociate,
    "044": salesAssociate,
    "045": salesAssociate,
    "046": salesAssociate,
    "047": salesAssociate,
    "048": salesAssociate,
    "049": salesAssociate,
    "050": salesAssociate,
    "051": salesAssociate,
    "052": salesAssociate,
    "053": salesAssociate,
    "054": salesAssociate,
    "055": salesAssociate,
    "060": salesAssociate,
    "062": salesAssociate,
    "063": salesAssociate,
    "065": salesAssociate,
    "066": salesAssociate,
    "067": salesAssociate,
    "068": salesAssociate,
    "069": salesAssociate,
    "070": salesAssociate,
    "077": salesAssociate,
    "081": salesAssociate,
    "098": salesAssociate,
    "099": salesAssociate,
    101: salesAssociate,
    102: salesAssociate,
    105: salesAssociate,
    108: salesAssociate,
    111: salesAssociate,
    112: salesAssociate,
    113: salesAssociate,
    114: salesAssociate,
    116: salesAssociate,
    117: salesAssociate,
    118: salesAssociate,
    119: salesAssociate,
    134: salesAssociate,
    135: salesAssociate,
    136: salesAssociate,
    137: salesAssociate,
    138: salesAssociate,
    140: salesAssociate,
    145: salesAssociate,
    146: salesAssociate,
    147: salesAssociate,
    148: salesAssociate,
    149: salesAssociate,
    150: salesAssociate,
    151: salesAssociate,
    152: salesAssociate,
    153: salesAssociate,
    154: salesAssociate,
    155: salesAssociate,
    156: salesAssociate,
    157: salesAssociate,
    159: salesAssociate,
    160: salesAssociate,
    161: salesAssociate,
    170: salesAssociate,
    171: salesAssociate,
    172: salesAssociate,
    174: salesAssociate,
    321: salesAssociate,
    322: salesAssociate,
  },
  ToyotaConnectedServicesUrl: toyotaConnectedServicesUrl,
};
